import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";
import { useMediaQuery, useTheme } from "@mui/material";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

// Shared table component
import DataTable from "components/DataTable";
import WeeklyAttendanceChart from "shell/WeeklyAttendanceChart";
import PageHeader from "components/PageHeader";

// Custom hooks
import useAttendanceData from "layouts/tables/data/attendanceReportTabledata";

function AttendanceReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { church } = useAuth();
  const [weeklyTrend, setWeeklyTrend] = useState([]);

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    fetch(`${API_BASE}/analytics/weekly-attendance?church_id=${church.id}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((rows) => setWeeklyTrend(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [church?.id]);

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    period,
    dateRange,
    totalMembers,
    presentCount,
    fetchAttendanceData,
    handleExport,
    setDateRange,
    handlePeriodChange,
    handleCustomDateChange,
    getReportTitle,
    selectedMember,
    showMemberDetails,
    closeMemberDetails,
    churchId,
    MemberDetailsModal: ModalComponent,
  } = useAttendanceData();

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "phone"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    handleCustomDateChange(newDateRange.startDate, newDateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    handleCustomDateChange(newDateRange.startDate, newDateRange.endDate);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchAttendanceData(newDateRange.startDate, newDateRange.endDate);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);
    handlePeriodChange(type);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Attendance Data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <PageHeader
        title="Attendance Report"
        subtitle={getReportTitle()}
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  maxDate={dateRange.endDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
                <DatePicker
                  label="To Date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  minDate={dateRange.startDate}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={{ width: 150 }} />
                  )}
                />
              </LocalizationProvider>
            )}

            <Button variant="outlined" onClick={handleTodayClick} sx={{ minWidth: "100px" }}>
              Today
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("csv")}
              startIcon={<Icon>download</Icon>}
            >
              CSV
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("pdf")}
              startIcon={<Icon>picture_as_pdf</Icon>}
            >
              PDF
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleExport("print")}
              startIcon={<Icon>print</Icon>}
            >
              Print
            </Button>
          </>
        }
      />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Attendance Trend — Last 7 Days
            </Typography>
            <WeeklyAttendanceChart data={weeklyTrend} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Present: {presentCount} | Absent: {totalMembers - presentCount} | Total:{" "}
              {totalMembers}
            </Typography>
            <Box pt={3}>
              <DataTable
                table={{
                  columns,
                  rows: rows.map((row) => ({
                    ...row,
                    // Ensure all data is included even if columns are hidden
                    originalData: row.originalData || {
                      id: row.id,
                      memberName: row.memberName,
                      isPresent: row.isPresent,
                      checkInTime: row.checkInTime,
                      method: row.method,
                      phone: row.phone,
                      email: row.email,
                    },
                  })),
                }}
                isSorted={false}
                entriesPerPage={true}
                showTotalEntries={true}
                noEndBorder
                canSearch={true}
                searchLabel="Search members..."
                printFullTable // Ensure all columns print regardless of screen size
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <ModalComponent
        member={selectedMember}
        open={showMemberDetails}
        onClose={closeMemberDetails}
        churchId={churchId}
      />
    </Box>
  );
}

export default AttendanceReport;
